import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { motion } from 'framer-motion';
import api from '../../api/axios';

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', code: '' });

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/categories');
      setCategories(data.categories || []);
    } catch (err) {
      toast.error('Erreur chargement catégories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleSave = async () => {
    if (!form.name || !form.code) {
      toast.error('Tous les champs sont requis');
      return;
    }

    try {
      if (editId) {
        await api.put(`/categories/${editId}`, form);
        toast.success('Catégorie modifiée');
      } else {
        await api.post('/categories', form);
        toast.success('Catégorie créée');
      }
      setForm({ name: '', code: '' });
      setEditId(null);
      setShowForm(false);
      fetchCategories();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur sauvegarde');
    }
  };

  const handleEdit = (cat) => {
    setEditId(cat.id);
    setForm({ name: cat.name, code: cat.code });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette catégorie ?')) return;
    try {
      await api.delete(`/categories/${id}`);
      toast.success('Catégorie supprimée');
      fetchCategories();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur suppression');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-qfl-dark-2 to-qfl-dark p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-qfl-gold">Gestion des Catégories</h1>
          <button
            onClick={() => {
              setForm({ name: '', code: '' });
              setEditId(null);
              setShowForm(!showForm);
            }}
            className="px-4 py-2 bg-qfl-gold text-black font-bold rounded-lg hover:bg-yellow-400 transition"
          >
            {showForm ? 'Annuler' : '+ Nouvelle'}
          </button>
        </div>

        {showForm && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-qfl-card border border-green-800 rounded-lg p-5 mb-6"
          >
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Nom de la catégorie"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-qfl-dark border border-green-700 rounded px-3 py-2 text-white"
              />
              <input
                type="text"
                placeholder="Code (minuscules)"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase() })}
                className="bg-qfl-dark border border-green-700 rounded px-3 py-2 text-white"
              />
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleSave}
                className="flex-1 px-4 py-2 bg-green-600 text-white font-bold rounded hover:bg-green-700 transition"
              >
                {editId ? 'Modifier' : 'Créer'}
              </button>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditId(null);
                  setForm({ name: '', code: '' });
                }}
                className="flex-1 px-4 py-2 bg-gray-600 text-white font-bold rounded hover:bg-gray-700 transition"
              >
                Annuler
              </button>
            </div>
          </motion.div>
        )}

        {loading ? (
          <div className="text-center text-gray-400">Chargement...</div>
        ) : (
          <div className="bg-qfl-card border border-green-800 rounded-lg overflow-hidden">
            <table className="w-full text-sm text-white">
              <thead className="bg-green-900/50 border-b border-green-800">
                <tr>
                  <th className="px-4 py-3 text-left">Nom</th>
                  <th className="px-4 py-3 text-left">Code</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((cat) => (
                  <tr key={cat.id} className="border-b border-green-800/50 hover:bg-green-900/20 transition">
                    <td className="px-4 py-3">{cat.name}</td>
                    <td className="px-4 py-3 font-mono text-gray-400">{cat.code}</td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <button
                        onClick={() => handleEdit(cat)}
                        className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition"
                      >
                        Modifier
                      </button>
                      <button
                        onClick={() => handleDelete(cat.id)}
                        className="px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 transition"
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
